import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Clock, 
  Edit,
  Trash2,
  Play,
  Check,
  X
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { scheduleService, Schedule, ScheduleInstance } from '../../services/scheduleService';
import { lessonService } from '../../services/lessonService';
import { Lesson } from '../../types';

interface ScheduleCalendarProps {
  teacherId: string;
  onScheduleClick?: (instance: ScheduleInstance) => void;
  onEditSchedule?: (schedule: Schedule) => void;
  onDeleteSchedule?: (scheduleId: string) => void;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  teacherId,
  onScheduleClick,
  onEditSchedule,
  onDeleteSchedule
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [instances, setInstances] = useState<ScheduleInstance[]>([]);
  const [lessons, setLessons] = useState<Map<string, Lesson>>(new Map());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [selectedInstance, setSelectedInstance] = useState<ScheduleInstance | null>(null);

  const loadData = useCallback(async () => {
    try {
      // Calculate date range
      const startDate = new Date(currentDate);
      const endDate = new Date(currentDate);
      
      if (viewMode === 'week') {
        startDate.setDate(startDate.getDate() - startDate.getDay());
        endDate.setDate(endDate.getDate() - endDate.getDay() + 6);
      } else {
        startDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
      }

      // Load schedules and instances
      const [fetchedSchedules, fetchedInstances, fetchedLessons] = await Promise.all([
        scheduleService.getSchedulesByTeacher(teacherId),
        scheduleService.getScheduleInstances(startDate, endDate, teacherId),
        lessonService.getAllLessons()
      ]);

      setSchedules(fetchedSchedules);
      setInstances(fetchedInstances);
      
      // Create lesson map
      const lessonMap = new Map<string, Lesson>();
      fetchedLessons.forEach(lesson => {
        if (lesson.metadata?.lesson_id) {
          lessonMap.set(lesson.metadata.lesson_id, lesson);
        }
      });
      setLessons(lessonMap);
    } catch (error) {
      console.error('Error loading schedule data:', error);
    }
  }, [teacherId, currentDate, viewMode]);

  // Load data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar days
  const getCalendarDays = () => {
    const days = [];
    const startDate = new Date(currentDate);
    
    if (viewMode === 'week') {
      startDate.setDate(startDate.getDate() - startDate.getDay());
      for (let i = 0; i < 7; i++) {
        const day = new Date(startDate);
        day.setDate(day.getDate() + i);
        days.push(day);
      }
    } else {
      startDate.setDate(1);
      const firstDayOfWeek = startDate.getDay();
      const lastDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      
      // Add previous month days
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const day = new Date(startDate);
        day.setDate(day.getDate() - i - 1);
        days.push(day);
      }
      
      // Add current month days
      for (let i = 1; i <= lastDate.getDate(); i++) {
        const day = new Date(startDate);
        day.setDate(i);
        days.push(day);
      }
      
      // Add next month days to complete the grid
      const remainingDays = 42 - days.length; // 6 rows * 7 days
      for (let i = 1; i <= remainingDays; i++) {
        const day = new Date(lastDate);
        day.setDate(lastDate.getDate() + i);
        days.push(day);
      }
    }
    
    return days;
  };

  // Get instances for a specific day
  const getInstancesForDay = (date: Date) => {
    return instances.filter(instance => {
      const instanceDate = new Date(instance.startDateTime);
      return instanceDate.toDateString() === date.toDateString();
    });
  };

  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Handle instance click
  const handleInstanceClick = (instance: ScheduleInstance) => {
    setSelectedInstance(instance);
    onScheduleClick?.(instance);
  };

  // Handle instance status update
  const handleStatusUpdate = async (instanceId: string, newStatus: ScheduleInstance['status']) => {
    try {
      await scheduleService.updateScheduleInstance(instanceId, { status: newStatus });
      await loadData(); // Reload data
      setSelectedInstance(null);
    } catch (error) {
      console.error('Error updating instance status:', error);
    }
  };



  const calendarDays = getCalendarDays();
  const dayNames = [t('schedule.sunday'), t('schedule.monday'), t('schedule.tuesday'), t('schedule.wednesday'), t('schedule.thursday'), t('schedule.friday'), t('schedule.saturday')];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('schedule.calendar')}</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'week' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t('schedule.week')}
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'month' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t('schedule.month')}
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPrevious}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            {t('schedule.today')}
          </button>
          <button
            onClick={goToNext}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {dayNames.map(day => (
              <div key={day} className="p-3 text-center text-sm font-medium text-gray-700">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = day.toDateString() === new Date().toDateString();
              const dayInstances = getInstancesForDay(day);
              
              return (
                <div
                  key={index}
                  className={`min-h-32 border-r border-b border-gray-200 p-2 ${
                    !isCurrentMonth ? 'bg-gray-50' : ''
                  }`}
                >
                  {/* Day Number */}
                  <div className={`text-sm font-medium mb-2 ${
                    isToday 
                      ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center' 
                      : isCurrentMonth 
                        ? 'text-gray-900' 
                        : 'text-gray-400'
                  }`}>
                    {day.getDate()}
                  </div>

                  {/* Schedule Instances */}
                  <div className="space-y-1">
                    {dayInstances.slice(0, 3).map((instance) => {
                      const schedule = schedules.find(s => s.id === instance.scheduleId);
                      const lesson = lessons.get(instance.lessonId);
                      
                      return (
                        <motion.div
                          key={instance.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleInstanceClick(instance)}
                          className={`p-2 rounded-md text-xs cursor-pointer border transition-colors ${getStatusColor(instance.status)}`}
                        >
                          <div className="font-medium truncate">
                            {schedule?.title || lesson?.metadata?.title || t('schedule.untitled')}
                          </div>
                          <div className="flex items-center space-x-1 text-xs opacity-75">
                            <Clock className="w-3 h-3" />
                            <span>{formatTime(instance.startDateTime)}</span>
                          </div>
                        </motion.div>
                      );
                    })}
                    
                    {dayInstances.length > 3 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{dayInstances.length - 3} {t('schedule.more')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Instance Details Modal */}
      {selectedInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('schedule.scheduleDetails')}
              </h3>
              <button
                onClick={() => setSelectedInstance(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">
                  {schedules.find(s => s.id === selectedInstance.scheduleId)?.title || t('schedule.untitled')}
                </h4>
                <p className="text-sm text-gray-600">
                  {lessons.get(selectedInstance.lessonId)?.metadata?.title || t('schedule.unknownLesson')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{selectedInstance.startDateTime.toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{formatTime(selectedInstance.startDateTime)} - {formatTime(selectedInstance.endDateTime)}</span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedInstance.status)}`}>
                  {t(`schedule.status.${selectedInstance.status}`)}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  {selectedInstance.status === 'scheduled' && (
                    <button
                      onClick={() => {
                        handleStatusUpdate(selectedInstance.id!, 'in_progress');
                        // Navigate to the lesson session with schedule instance ID
                        navigate(`/session/${selectedInstance.lessonId}?scheduleInstanceId=${selectedInstance.id}`);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700 transition-colors"
                    >
                      <Play className="w-3 h-3" />
                      <span>{t('schedule.start')}</span>
                    </button>
                  )}
                  
                  {selectedInstance.status === 'in_progress' && (
                    <button
                      onClick={() => handleStatusUpdate(selectedInstance.id!, 'completed')}
                      className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                    >
                      <Check className="w-3 h-3" />
                      <span>{t('schedule.complete')}</span>
                    </button>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const schedule = schedules.find(s => s.id === selectedInstance.scheduleId);
                      if (schedule) onEditSchedule?.(schedule);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (onDeleteSchedule) onDeleteSchedule(selectedInstance.scheduleId);
                      setSelectedInstance(null);
                    }}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
